import {parse, stringify, parseUrl, stringifyUrl} from 'query-string';
import {format, parseISO} from 'date-fns';
import {v4 as uuidv4} from 'uuid';
import {EMAIL_REGEXP, PHONE_NUMBER_REGEXP, PASSWORD_RULES, LOCAL_STORAGE_ITEMS} from './constants';
import {gettext} from './translationService';
import {Primitive} from './generalTypes';

export const pipe = (...funcs) => {
    const _pipe = (prevFunc, currFunc) => (...arg) => currFunc(prevFunc(...arg));

    return funcs.reduce(_pipe);
};

export const equal = (val, other) => val === other;

export const compose = (...funcs) => pipe(...funcs.reverse());

export const pass = val => val;

export const negate = val => !val;

export const negateFunc = func => pipe(func, negate);

export const isPrimitive = val => val !== Object(val);

export const isBoolean = val => equal(typeof val, 'boolean');

export const isEmail = value => EMAIL_REGEXP.test(value);

export const isFunction = val => equal(typeof val, 'function');

export const isEmpty = obj => {
    if (isPrimitive(obj)) {
        return !obj;
    }
    if (Array.isArray(obj)) {
        return !obj.length;
    }

    return isEmpty(Object.keys(obj));
};

export const partial = (func, ...params) => (...args) => func(...params, ...args);

export const getItemKeyValue = key => obj => obj[key];

export const bytesToMegabytes = bytes => bytes / 1024 / 1000;

export const getEqual = (value: Primitive, key?: string) => {
    return pipe(key ? getItemKeyValue(key) : pass, partial(equal, value));
};

export const getMatches = (rules: {[key: string]: RegExp}, value: string = ''): {[key in keyof typeof rules]: RegExpMatchArray} => {
    return Object
        .entries(rules)
        .reduce((acc, [key, pattern]) => ({...acc, [key]: value.match(pattern)}), {});
};

export const toCapitalize = str => str && (str.charAt(0).toUpperCase() + str.slice(1).toLowerCase());

export const validateEmail = email => !isEmail(email) ? gettext('Please enter a valid email') : undefined;

export const validatePhoneNumber = phone => !PHONE_NUMBER_REGEXP.test(phone) ? gettext('Please enter a valid phone number') : undefined;

export const validateRequired = value => isEmpty(value) ? 'Required' : undefined;

export const validateFileSize = (value, maxSize) => {
    const errorMessage = `Your uploaded file is too big. Maximum file size is: ${maxSize}Mb`;

    const files = [].concat(value).filter((item: File) => item instanceof File);
    const isInvalidSize = files.some(({size}) => bytesToMegabytes(size) > maxSize);

    return isInvalidSize ? errorMessage : undefined;
};

export const validateStrongPassword = password => !Object.values(getMatches(PASSWORD_RULES, password)).every(Boolean) ? ' ' : undefined;

export const generateUniqueId = () => uuidv4();

export const getDeviceId = () => {
    const deviceId = localStorage.getItem(LOCAL_STORAGE_ITEMS.deviceId);

    if (deviceId) {
        return deviceId;
    }

    const generatedDeviceId = generateUniqueId();
    localStorage.setItem(LOCAL_STORAGE_ITEMS.deviceId, generatedDeviceId);

    return generatedDeviceId;
};

export const parseQuery = (query, options = {}) => parse(query, {...options, arrayFormat: 'comma'});

export const stringifyQueryParams = params => stringify(params, {arrayFormat: 'comma'});

export const parseURL = url => parseUrl(url);

export const stringifyURL = (data, options = {}) => stringifyUrl({...data}, options);

export const formatPhone = phone => phone && `${phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')}`;

export const formatDate = (date, dateFormat, options = {}) => date && format(date, dateFormat, options);

export const parseISODate = (date, options = {}) => date && parseISO(date, options);
